/**
 * Chrome (and other) page translators mutate the live DOM by wrapping text nodes
 * in extra elements (often <font>). React's reconciler then throws when it tries
 * removeChild / insertBefore against the tree it thinks it owns — a common crash
 * when selecting dates in react-dates / Mantine date pickers.
 *
 * Soften those DOM ops so translation cannot white-screen the app.
 * See: https://github.com/facebook/react/issues/11538
 */
export function installGoogleTranslateCompat(): void {
  if (typeof Node !== 'function' || !Node.prototype) {
    return;
  }

  const proto = Node.prototype as Node & {
    __nifoniGoogleTranslateCompat?: boolean;
  };

  if (proto.__nifoniGoogleTranslateCompat) {
    return;
  }
  proto.__nifoniGoogleTranslateCompat = true;

  const originalRemoveChild = proto.removeChild;
  proto.removeChild = function <T extends Node>(child: T): T {
    if (child.parentNode !== this) {
      if (child.parentNode) {
        return originalRemoveChild.call(child.parentNode, child) as T;
      }
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = proto.insertBefore;
  proto.insertBefore = function <T extends Node>(newNode: T, referenceNode: Node | null): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      // Reference was moved by the translator; append so React can keep going.
      return this.appendChild(newNode);
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
